import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import { canRequestCustomerShipmentCancellation } from "./shipment-action-eligibility";

const API_URL = import.meta.env.VITE_API_URL;

interface RequestShipmentCancellationResponse {
  success: boolean;
  message: string;
  shipment: {
    id: string;
    shipmentNumber: number;
    cancellationRequestedAt: string;
  };
}

interface CancelAllShipmentsResponse {
  success: boolean;
  message: string;
  results: {
    successful: string[];
    failed: { shipmentId: string; error: string }[];
  };
}

/**
 * Request cancellation for a single shipment
 */
export function useRequestShipmentCancellation(options?: {
  onSuccess?: (data: RequestShipmentCancellationResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({
      shipmentId,
      reason,
    }: {
      shipmentId: string;
      reason?: string;
    }) => {
      const { data } = await axios.post<RequestShipmentCancellationResponse>(
        `${API_URL}/api/v2/shipments/${shipmentId}/request-cancellation`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

/**
 * Request cancellation for all shipments in an order
 * This is the main hook customers use when clicking "Cancel Order"
 */
export function useRequestCancellation(options?: {
  onSuccess?: (data: CancelAllShipmentsResponse) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({
      orderId,
      reason,
    }: {
      orderId: string;
      reason?: string;
    }) => {
      // First, get the order with shipments
      const orderResponse = await axios.get<{
        id: string;
        shipments: { id: string; status: string }[];
      }>(`${API_URL}/api/v2/orders/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const shipments = orderResponse.data.shipments || [];
      // Filter to only cancellable shipments
      const cancellableShipments = shipments.filter((s) =>
        canRequestCustomerShipmentCancellation(s.status)
      );

      if (cancellableShipments.length === 0) {
        throw new Error("No shipments can be cancelled");
      }

      // Request cancellation for each shipment
      const results: {
        successful: string[];
        failed: { shipmentId: string; error: string }[];
      } = {
        successful: [],
        failed: [],
      };

      await Promise.all(
        cancellableShipments.map(async (shipment) => {
          try {
            await axios.post(
              `${API_URL}/api/v2/shipments/${shipment.id}/request-cancellation`,
              { reason },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            results.successful.push(shipment.id);
          } catch (err) {
            const error = err as { response?: { data?: { error?: string } } };
            results.failed.push({
              shipmentId: shipment.id,
              error: error.response?.data?.error ?? "Unknown error",
            });
          }
        })
      );

      if (results.successful.length === 0) {
        throw new Error(
          results.failed.map((failure) => failure.error).join("; ")
        );
      }
      return {
        success: results.successful.length > 0,
        message:
          results.successful.length === cancellableShipments.length
            ? `Cancellation requested for ${results.successful.length} shipment(s)`
            : `Cancellation requested for ${results.successful.length} of ${cancellableShipments.length} shipment(s)`,
        results,
      };
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
